import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Add, Delete } from "@mui/icons-material";
import { Alert, Button, Divider, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { DataField } from "@/components/DataField";
import { CrudModal } from "@/components/crud/CrudModal";
import { DiaDoMesField, dataNaCompetencia } from "@/components/escala/DiaDoMesField";
import { juntarComE } from "@/components/escala/cargaHoraria";
import {
  ANO_MAXIMO_COMPETENCIA,
  ANO_MINIMO_COMPETENCIA,
  formatDataBR,
  MESES,
  type Colaborador,
  type DiaCheioUnico,
  type Escala,
  type FolgaCompensatoria,
  type Impedimento,
} from "@/api/escala.service";

// GerarValues é escala.gerar's payload (04-contratos.md) sem escala_id —
// isso vem de qual escala o modal foi aberto, não do formulário, mesmo
// raciocínio de EscalaVinculoValues.
export interface GerarValues {
  ano: number;
  mes: number;
  quantidade_minima: number;
  // Em branco, o domingo usa quantidade_minima e a jornada é a dos Parâmetros.
  quantidade_minima_domingo?: number;
  jornada_semanal?: number;
  dias_cheios_unicos: DiaCheioUnico[];
  folgas_compensatorias: FolgaCompensatoria[];
}

const QUANTIDADE_MINIMA_MINIMA = 0;

// Teto da jornada da escala: 44h mais 2h extras por dia em 6 dias (motor.MaxJornadaSemanalComExtra).
export const JORNADA_SEMANAL_MAXIMA = 56;

// validarGerarValues espelha, no cliente, as validações que
// internal/escala/precondicoes.go (trava 1) e internal/escala/competencia.go
// (validateDiasCheiosUnicos/validateFolgasCompensatorias) aplicam no
// servidor — sem isso, cada campo vazio ou duplicado só aparece depois de
// uma ida e volta ao backend. O backend continua sendo a autoridade (as
// mesmas checagens rodam de novo lá); isto é só pra travar antes e dar uma
// mensagem coerente com a de lá, em vez de deixar o usuário descobrir pelo
// erro de resposta.
export function validarGerarValues(values: GerarValues): string | null {
  if (!values.mes || values.mes < 1 || values.mes > 12) {
    return "Mês inválido: informe de 1 a 12";
  }
  if (!values.ano || values.ano < ANO_MINIMO_COMPETENCIA || values.ano > ANO_MAXIMO_COMPETENCIA) {
    return "Ano inválido: informe o ano da competência com quatro dígitos";
  }
  if (values.quantidade_minima == null || values.quantidade_minima < QUANTIDADE_MINIMA_MINIMA) {
    return "A quantidade mínima de colaboradores por dia não pode ser negativa";
  }
  if (values.quantidade_minima_domingo != null && values.quantidade_minima_domingo < QUANTIDADE_MINIMA_MINIMA) {
    return "A quantidade mínima de colaboradores no domingo não pode ser negativa";
  }
  const jornada = values.jornada_semanal;
  if (jornada != null && (jornada < 1 || jornada > JORNADA_SEMANAL_MAXIMA)) {
    return `A jornada semanal da escala deve estar entre 1 e ${JORNADA_SEMANAL_MAXIMA} horas`;
  }

  const datasDiaCheio = new Set<string>();
  for (const dc of values.dias_cheios_unicos) {
    if (!dc.data) return "Preencha a data de todo dia cheio único";
    if (!dataNaCompetencia(dc.data, values.ano, values.mes)) {
      return `Dia cheio único de ${formatDataBR(dc.data)} está fora da competência ${String(values.mes).padStart(2, "0")}/${values.ano}`;
    }
    if (!dc.descricao.trim()) return `Dia cheio único de ${formatDataBR(dc.data)} precisa de descrição`;
    if (datasDiaCheio.has(dc.data)) return `Dia cheio único de ${formatDataBR(dc.data)} cadastrado duas vezes`;
    datasDiaCheio.add(dc.data);
  }

  const paresFolgaCompensatoria = new Set<string>();
  for (const fc of values.folgas_compensatorias) {
    if (!fc.colaborador_id) return "Selecione o colaborador de toda folga compensatória";
    if (!fc.data_feriado || !fc.data_folga) return "Preencha as duas datas de toda folga compensatória";
    if (fc.data_feriado === fc.data_folga) return "A folga compensatória não pode ser no mesmo dia do feriado";
    if (!dataNaCompetencia(fc.data_folga, values.ano, values.mes)) {
      return `Folga compensatória de ${formatDataBR(fc.data_folga)} está fora da competência ${String(values.mes).padStart(2, "0")}/${values.ano}`;
    }
    const chave = `${fc.colaborador_id}|${fc.data_folga}`;
    if (paresFolgaCompensatoria.has(chave)) {
      return "Existe folga compensatória cadastrada duas vezes para o mesmo colaborador e data";
    }
    paresFolgaCompensatoria.add(chave);
  }

  return null;
}

// REGRA_IMPEDIMENTO_LABEL traduz as siglas de motor.Impedimento.Regra
// (internal/escala/motor/viabilidade.go) — "strings estáveis, a tela
// traduz, o motor nunca monta frase" é o comentário de lá.
const REGRA_IMPEDIMENTO_LABEL: Record<string, string> = {
  PARAMETRO_INVALIDO: "Um parâmetro da Escala está fora da faixa válida — revise em Parâmetros",
  SEMANA_SEM_DIA_LIVRE: "Uma semana não tem nenhum dia disponível para folga",
  GRUPO_MAIOR_QUE_OS_DIAS: "Um grupo estratégico tem mais gente do que dias no mês",
  GRUPO_MAIOR_QUE_A_SEMANA:
    "Um grupo estratégico não cabe numa semana, com poucos dias livres (reduza o grupo ou tire o dia cheio)",
};

const REGRA_AUSENCIA_NO_GRUPO = "AUSENCIA_NO_GRUPO";

// A ausência no grupo vem com as pessoas e o trecho, e a frase diz o que fazer.
export function descreverAusenciaNoGrupo(imp: Impedimento, colaboradores: Colaborador[]): string {
  const nome = (id?: string) => colaboradores.find((c) => c.id === id)?.nome ?? "um colaborador do grupo";
  const ausentes = juntarComE((imp.ausente_ids ?? []).map(nome));
  const ficam = (imp.ausente_ids?.length ?? 0) > 1 ? "ficam" : "fica";
  const trecho =
    imp.data && imp.ate && imp.data !== imp.ate
      ? `de ${formatDataBR(imp.data)} a ${formatDataBR(imp.ate)}`
      : `em ${formatDataBR(imp.data ?? "")}`;
  return (
    `Grupo estratégico: ${ausentes} ${ficam} fora ${trecho}, e nesses dias ${nome(imp.colaborador_id)} ` +
    `não pode folgar sem passar do limite da lei. Tire ${ausentes} do grupo nesta escala ` +
    `(e recoloque quando voltar) ou ponha outro no lugar.`
  );
}

// useListField generaliza o add/change/remove de um campo-lista de
// GerarValues (dias_cheios_unicos, folgas_compensatorias) — os dois tinham
// exatamente os mesmos três handlers, só trocando o nome do campo.
function useListField<K extends "dias_cheios_unicos" | "folgas_compensatorias">(
  setValues: Dispatch<SetStateAction<Partial<GerarValues>>>,
  field: K,
) {
  type Item = GerarValues[K][number];
  const add = (item: Item) => {
    setValues((v) => ({ ...v, [field]: [...((v[field] as Item[]) ?? []), item] }));
  };
  const change = (index: number, patch: Partial<Item>) => {
    setValues((v) => ({
      ...v,
      [field]: ((v[field] as Item[]) ?? []).map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };
  const remove = (index: number) => {
    setValues((v) => ({ ...v, [field]: ((v[field] as Item[]) ?? []).filter((_, i) => i !== index) }));
  };
  return { add, change, remove };
}

interface EscalaGerarFormProps {
  values: Partial<GerarValues>;
  setValues: Dispatch<SetStateAction<Partial<GerarValues>>>;
  isEditing: boolean;
  colaboradores: Colaborador[];
  semCarga: Colaborador[];
  impedimentos: Impedimento[] | null;
  // A jornada só conta na escala com carga horária.
  comCarga: boolean;
}

// Campo numérico opcional: apagar deixa em branco, não zero.
const numeroOpcional = (texto: string) => (texto === "" ? undefined : Number(texto));

function EscalaGerarForm({ values, setValues, colaboradores, semCarga, impedimentos, comCarga }: EscalaGerarFormProps) {
  const ano = values.ano;
  const mes = values.mes;

  const diasCheiosUnicos = values.dias_cheios_unicos ?? [];
  const folgasCompensatorias = values.folgas_compensatorias ?? [];

  const diaCheioUnicoField = useListField(setValues, "dias_cheios_unicos");
  const folgaCompensatoriaField = useListField(setValues, "folgas_compensatorias");

  return (
    <>
      {semCarga.length > 0 && (
        <Alert severity="warning" sx={{ typography: "caption" }}>
          {semCarga.length} colaborador(es) vinculado(s) não têm uma das cargas horárias desta escala e
          ficam de fora da geração: {semCarga.map((c) => c.nome).join(", ")}.
        </Alert>
      )}
      {impedimentos && impedimentos.length > 0 && (
        <Alert severity="error" sx={{ typography: "body2" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Esta competência não fecha — nada foi gerado
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
            {impedimentos.map((imp, i) =>
              imp.regra === REGRA_AUSENCIA_NO_GRUPO ? (
                <li key={i}>{descreverAusenciaNoGrupo(imp, colaboradores)}</li>
              ) : (
                <li key={i}>
                  {REGRA_IMPEDIMENTO_LABEL[imp.regra] ?? imp.regra}
                  {imp.data ? ` — ${formatDataBR(imp.data)}` : ""}
                  {imp.necessario > 0 || imp.permitido > 0
                    ? ` (necessário ${imp.necessario}, permitido ${imp.permitido})`
                    : ""}
                </li>
              ),
            )}
          </Stack>
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        <TextField
          label="Ano"
          type="number"
          fullWidth
          required
          value={ano ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, ano: Number(e.target.value) }))}
          slotProps={{ htmlInput: { min: ANO_MINIMO_COMPETENCIA, max: ANO_MAXIMO_COMPETENCIA, step: 1 } }}
        />
        <TextField
          select
          label="Mês"
          fullWidth
          required
          value={mes ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, mes: Number(e.target.value) }))}
        >
          {MESES.map((nome, i) => (
            <MenuItem key={i + 1} value={i + 1}>{nome}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" spacing={2}>
        <TextField
          label="Quantidade mínima"
          type="number"
          fullWidth
          required
          helperText="Por dia, de segunda a sábado"
          value={values.quantidade_minima ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, quantidade_minima: Number(e.target.value) }))}
          slotProps={{ htmlInput: { min: QUANTIDADE_MINIMA_MINIMA, step: 1 } }}
        />
        <TextField
          label="Mínimo no domingo"
          type="number"
          fullWidth
          helperText="Em branco, o mesmo de seg a sáb"
          value={values.quantidade_minima_domingo ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, quantidade_minima_domingo: numeroOpcional(e.target.value) }))}
          slotProps={{ htmlInput: { min: QUANTIDADE_MINIMA_MINIMA, step: 1 } }}
        />
        {comCarga && (
          <TextField
            label="Jornada semanal (h)"
            type="number"
            fullWidth
            helperText="Em branco, a dos Parâmetros"
            value={values.jornada_semanal ?? ""}
            onChange={(e) => setValues((v) => ({ ...v, jornada_semanal: numeroOpcional(e.target.value) }))}
            slotProps={{ htmlInput: { min: 1, max: JORNADA_SEMANAL_MAXIMA, step: 1 } }}
          />
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2">Dias cheios únicos</Typography>
      <Typography variant="caption" color="text.secondary">
        Datas específicas em que ninguém folga. Se existir um dia cheio único em qualquer data da
        semana, os dias cheios fixos daquela semana inteira são ignorados.
      </Typography>
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {diasCheiosUnicos.map((dc, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
            <DiaDoMesField
              label="Data do dia cheio"
              ano={ano}
              mes={mes}
              iso={dc.data}
              onChangeIso={(iso) => diaCheioUnicoField.change(i, { data: iso })}
              size="small"
            />
            <TextField
              label="Descrição"
              fullWidth
              required
              size="small"
              value={dc.descricao}
              onChange={(e) => diaCheioUnicoField.change(i, { descricao: e.target.value })}
              slotProps={{ htmlInput: { maxLength: 120 } }}
            />
            <IconButton aria-label="Remover dia cheio único" onClick={() => diaCheioUnicoField.remove(i)}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => diaCheioUnicoField.add({ data: "", descricao: "" })}
          sx={{ alignSelf: "flex-start" }}
        >
          Adicionar dia cheio único
        </Button>
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2">Folgas compensatórias</Typography>
      <Typography variant="caption" color="text.secondary">
        Par feriado trabalhado → dia de folga, por colaborador. A data da folga vira FA na grade e o
        gerador a respeita: ela conta no teto de folgas do dia e interrompe a sequência de dias
        trabalhados. O feriado pode ser de outro mês; a folga precisa estar no mês gerado.
      </Typography>
      {colaboradores.length === 0 && (
        <Alert severity="info" sx={{ typography: "caption" }}>
          Nenhum colaborador vinculado a esta escala ainda — vincule a equipe antes de cadastrar
          folgas compensatórias.
        </Alert>
      )}
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {folgasCompensatorias.map((fc, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
            <SelectEntidade
              label="Colaborador"
              options={colaboradores}
              value={fc.colaborador_id}
              onChange={(id) => folgaCompensatoriaField.change(i, { colaborador_id: id ?? "" })}
              getLabel={(c) => c.nome}
              required
              size="small"
            />
            <DataField
              label="Data do feriado"
              size="small"
              fullWidth
              required
              value={fc.data_feriado}
              onChange={(data) => folgaCompensatoriaField.change(i, { data_feriado: data })}
            />
            <DiaDoMesField
              label="Data da folga"
              ano={ano}
              mes={mes}
              iso={fc.data_folga}
              onChangeIso={(iso) => folgaCompensatoriaField.change(i, { data_folga: iso })}
              size="small"
            />
            <IconButton aria-label="Remover folga compensatória" onClick={() => folgaCompensatoriaField.remove(i)}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => folgaCompensatoriaField.add({ colaborador_id: "", data_feriado: "", data_folga: "" })}
          disabled={colaboradores.length === 0}
          sx={{ alignSelf: "flex-start" }}
        >
          Adicionar folga compensatória
        </Button>
      </Stack>
    </>
  );
}

interface EscalaGerarModalProps {
  escala: Escala | null;
  // A equipe que entra na geração; numa escala com carga horária, só quem tem uma das cargas dela.
  colaboradores: Colaborador[];
  // Vinculados ativos que ficam de fora por não ter uma das cargas da escala.
  semCarga?: Colaborador[];
  // A jornada da última geração desta escala, que o campo já traz preenchida.
  jornadaAnterior?: number;
  onClose: () => void;
  // Devolve a lista de impedimentos quando a competência não fecha (a
  // geração não persiste nada — 01-dominio.md pré-condição 7); devolve null
  // quando a geração deu certo, e aí quem fecha o modal é quem chamou
  // (mesma responsabilidade de notify/log/loadData que o resto da tela).
  onSubmit: (values: GerarValues) => Promise<Impedimento[] | null>;
}

// Ano/mês por padrão abrem no mês corrente — quem gera normalmente está
// gerando a competência que está prestes a começar ou já começou, não uma
// data em branco pra escolher do zero toda vez.
function valoresIniciais(jornadaAnterior?: number): GerarValues {
  const agora = new Date();
  return {
    ano: agora.getFullYear(),
    mes: agora.getMonth() + 1,
    quantidade_minima: 0,
    jornada_semanal: jornadaAnterior,
    dias_cheios_unicos: [],
    folgas_compensatorias: [],
  };
}

export function EscalaGerarModal({ escala, colaboradores, semCarga = [], jornadaAnterior, onClose, onSubmit }: EscalaGerarModalProps) {
  const [impedimentos, setImpedimentos] = useState<Impedimento[] | null>(null);

  // Travado em escala?.id, não recalculado a cada render: CrudModal reseta
  // `values` sempre que a referência de `initialData` muda (seu useEffect
  // depende dela), então um objeto novo a cada render apagaria o que o
  // usuário digitou assim que EscalaEscalasTab re-renderizasse por
  // qualquer outro motivo com o modal ainda aberto.
  const initialData = useMemo(() => (escala ? valoresIniciais(jornadaAnterior) : null), [escala?.id, jornadaAnterior]);

  // Reabrir pra outra escala (ou fechar) limpa o impedimento da tentativa
  // anterior — não é erro que se carrega de uma geração pra outra.
  useEffect(() => {
    setImpedimentos(null);
  }, [escala?.id]);

  // Sem guarda de "campo vazio" aqui: passa os valores adiante do jeito que
  // estiverem (ano/mês podem chegar 0 se o usuário apagou o campo) e deixa
  // quem chama (EscalaEscalasTab's onSubmitGerar, com validarGerarValues)
  // notificar o motivo — um retorno silencioso aqui deixaria o clique em
  // "Gerar" não fazer nada, sem explicação.
  const handleSubmit = async (values: Partial<GerarValues>) => {
    const resultado = await onSubmit({
      ano: values.ano ?? 0,
      mes: values.mes ?? 0,
      quantidade_minima: values.quantidade_minima ?? 0,
      quantidade_minima_domingo: values.quantidade_minima_domingo,
      // A escala sem carga não mede horas: não manda a jornada.
      jornada_semanal: escala?.com_carga_horaria ? values.jornada_semanal : undefined,
      dias_cheios_unicos: values.dias_cheios_unicos ?? [],
      folgas_compensatorias: values.folgas_compensatorias ?? [],
    });
    setImpedimentos(resultado);
  };

  return (
    <CrudModal<
      GerarValues,
      { colaboradores: Colaborador[]; semCarga: Colaborador[]; impedimentos: Impedimento[] | null; comCarga: boolean }
    >
      open={!!escala}
      onClose={() => {
        setImpedimentos(null);
        onClose();
      }}
      title={`Gerar escala — ${escala?.nome ?? ""}`}
      initialData={initialData}
      onSubmit={handleSubmit}
      Form={EscalaGerarForm}
      extraFormProps={{ colaboradores, semCarga, impedimentos, comCarga: !!escala?.com_carga_horaria }}
      submitLabel="Gerar"
    />
  );
}
